import * as THREE from 'three';
import { TICK_HZ } from '@somnhold/content';
import { edgeShut, ROCA_HORN, WOLF_PILLAR, WOLF_STONE, wolfDropAt, type BattleState } from '@somnhold/sim';
import { simToWorld } from '@somnhold/render';
import { pinMark, remeasureMark } from './mark-pin.ts';
import { t } from '../i18n.ts';
import { h, setStyle } from '../ui/dom.ts';

/** A hunting horn. */
const HORN = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M2 11.5c3.6-.2 7.4-2.4 9.6-6.6l2.2 1.2c-2.2 4.8-6.8 7.6-11.6 7.6z" fill="currentColor"/><path d="M11.4 4.6l2.4-2.6 1 .8-2 2.9" fill="currentColor"/></svg>';
/** Ledges stepping up a wall. */
const LEDGE = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M1.5 14.5h4v-3h3.5v-3h3.5v-3h2v-3" stroke="currentColor" stroke-width="1.8" fill="none" stroke-linejoin="round"/></svg>';
/** An arch of rock over a mouth. */
const ARCH = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M2 14.5V8.5a6 6 0 0 1 12 0v6h-3v-6a3 3 0 0 0-6 0v6z" fill="currentColor"/></svg>';
/** A crack. */
const CRACK = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M3 2.5l3.4 3.2-1 2.6 3.6 1.4-.6 2.6L13 14" stroke="currentColor" stroke-width="1.7" fill="none" stroke-linecap="round" stroke-linejoin="round"/></svg>';
/** A wolf's paw print. */
const PAW = '<svg viewBox="0 0 16 16" aria-hidden="true"><ellipse cx="8" cy="10.5" rx="3.2" ry="2.8" fill="currentColor"/><circle cx="3.6" cy="6.4" r="1.4" fill="currentColor"/><circle cx="6.2" cy="4" r="1.4" fill="currentColor"/><circle cx="9.8" cy="4" r="1.4" fill="currentColor"/><circle cx="12.4" cy="6.4" r="1.4" fill="currentColor"/></svg>';
/** The Grey Wolf's head. */
const WOLF = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M3 13.5l1.8-5.2L3.6 4l2.6 1.8L9.4 2l.8 3.4 3.3 1.4-2.7 1.4L9 13.5z" fill="currentColor"/></svg>';
/** A howl. */
const HOWL = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M3 13.5l1.8-5.2L3.6 4l2.6 1.8L9.4 2l.8 3.4 3.3 1.4-2.7 1.4L9 13.5z" fill="currentColor"/><path d="M12.8 2.6c.9.7 1.4 1.6 1.4 2.6M11.9 1.2c1.5.9 2.4 2.4 2.5 4" stroke="currentColor" stroke-width="1" fill="none" stroke-linecap="round"/></svg>';
/** Dark water (a way out). */
const WATER = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M1 9c2 0 2-1.6 4-1.6S7 9 9 9s2-1.6 4-1.6 2 1.6 2 1.6v1.5c-2 0-2-1.6-4-1.6s-2 1.6-4 1.6-2-1.6-4-1.6-2 1.6-2 1.6z" fill="currentColor"/><path d="M1 12.5c2 0 2-1.6 4-1.6s2 1.6 4 1.6 2-1.6 4-1.6 2 1.6 2 1.6V14H1z" fill="currentColor" opacity=".6"/></svg>';
/** A pillar of the roof, cracked. */
const PILLAR = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M5 1.5h6v2H10v9h1v2H5v-2h1v-9H5z" fill="currentColor"/><path d="M7 5l1.4 1.8L7.2 9l1.4 1.6" stroke="#14161b" stroke-width="1" fill="none"/></svg>';
/** A loose stone. */
const STONE = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M2.5 11.5l2-5 4-2.5 4.5 2 1 4.5-3 2.5H5z" fill="currentColor"/></svg>';
/** A boat. */
const BOAT = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M1 9h14l-2.6 3.6H3.6z" fill="currentColor"/><path d="M8 2v6.4M8 2.4l4.4 4.6H8" stroke="currentColor" stroke-width="1.2" fill="none"/></svg>';
/** Ash and bones heaped (a way shut). */
const BONES = '<svg viewBox="0 0 16 16" aria-hidden="true"><ellipse cx="8" cy="12.5" rx="6.5" ry="2.3" fill="currentColor"/><path d="M3.5 9.5l9-4M4 5.5l8 4.5" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>';
/** The leap's shadow. */
const LEAP = '<svg viewBox="0 0 16 16" aria-hidden="true"><ellipse cx="8" cy="12" rx="6" ry="2.4" fill="currentColor" opacity=".6"/><path d="M3 9l3-5 3 2.4L13 3" stroke="currentColor" stroke-width="1.6" fill="none" stroke-linecap="round" stroke-linejoin="round"/></svg>';

type Mark = { el: HTMLButtonElement; state: HTMLElement; point: THREE.Vector3; at: number; kind: string; x: number; y: number; lift: number; chip?: HTMLElement };

const secs = (ticks: number): number => Math.max(0, Math.ceil(ticks / TICK_HZ));

/**
 * The black rock on screen (docs/sendas/barbaro.md, chapter 5), inside a path's own plate (game/senda-hud.ts) and over
 * the board, a phone's plate kept short:
 *
 * - the climb (`climb`): Ulla's life (she is the act's), the ledges climbed, the howler, the horn. Over the board, the
 *   horn over Ulla (a tap blows it; once) and the cave's mouth.
 * - the mouths (`mouths`): Ulla, the mouths shut of the two, the horn, the rule. Over the board, each arch (a tap
 *   strikes it once): its cracks (one, two; the third brings it down), «shut»; and, the hero come near it, what its
 *   tracks say (the Wolf's and the pack's; only the pack's).
 * - the ash bed (`bed`): the chamber, what is left of its band, the way on. Over the board, the heaps that shut the way.
 * - the Grey Wolf (`wolf`): its life, what it is doing (fighting, eyeing a way out, running for it, under the water,
 *   reeling, bristling, howling, crouched to leap, up the crack, down for the boat), its phase, the next leap, the horn,
 *   the boats, Sten's boat. Over the board, the way out it eyes in red (held at the screen's edge), the open mouths
 *   (the one without tracks: «no heal»), the pillars and the stones (a tap brings one down on it), the summit's edges
 *   (the boats', the island's), Sten's boat.
 *
 * Plain DOM, written only when it changes (docs/RENDIMIENTO.md); the board's points from the sim's own rules, lifted to
 * the rock's ground (the render's `ground`).
 */
export class RocaHud {
  readonly panel: HTMLElement;
  private kind: 'climb' | 'mouths' | 'bed' | 'wolf';
  private v = new THREE.Vector3();
  private rows: HTMLElement[] = [];
  private marks: Mark[] = [];
  private key = '';
  private read = new Set<number>();
  private ullaRow = h('div.bh-row.bt-boatrow.rk-ulla');
  private ullaFill = h('i');
  private ullaText = h('small.vt-boat-s');
  private stepRow = h('div.bh-row.al-waverow.rk-step');
  private stepPips: HTMLElement[] = [];
  private stepText = h('span.al-wave');
  private hornRow = h('div.bh-row.al-waverow.rk-horn');
  private hornText = h('span.al-wave');
  private howlRow = h('div.bh-row.al-waverow.bt-leap.rk-howl');
  private howlText = h('span.al-wave');
  private wolfRow = h('div.bh-row.bt-elder.rk-wolf');
  private wolfFill = h('i');
  private wolfText = h('small.vt-boat-s');
  private leapRow = h('div.bh-row.al-waverow.bt-leap.rk-leap');
  private leapText = h('span.al-wave');
  private boatRow = h('div.bh-row.al-waverow.rk-boats');
  private boatText = h('span.al-wave');
  private skiffRow = h('div.bh-row.bt-boatrow.rk-skiff');
  private skiffFill = h('i');
  private skiffText = h('small.vt-boat-s');

  constructor(
    s: BattleState,
    private board: HTMLElement,
    private mark: (at: number) => void,
    private ground: (x: number, y: number) => number = () => 0,
  ) {
    const rr = s.rules?.senda?.roca;
    this.kind = rr?.climb ? 'climb' : rr?.mouths ? 'mouths' : rr?.bed ? 'bed' : 'wolf';
    const rule = (icon: string, key: string): HTMLElement => h('div.bh-row.vt-rule', {}, h('span.bh-ico', { html: icon }), h('span.vt-rule-t', {}, t(key)));
    this.ullaRow.append(h('span.bh-ico', { html: HORN }), h('span.bh-label', {}, t('senda.barbarian.npc.ulla')), h('span.bar.vt-bar', {}, this.ullaFill), this.ullaText);
    this.hornRow.append(h('span.bh-ico', { html: HORN }), this.hornText);
    if (rr?.climb) {
      const r = rr.climb;
      this.stepPips = r.ledges.map(() => h('i'));
      this.stepRow.append(h('span.bh-ico', { html: LEDGE }), h('span.al-pips.vt-pips', {}, ...this.stepPips), this.stepText);
      this.howlRow.append(h('span.bh-ico', { html: HOWL }), this.howlText);
      this.rows.push(this.ullaRow, this.stepRow, this.howlRow, this.hornRow, rule(HORN, 'senda.roca.rule1'));
      this.marks.push(this.make(ROCA_HORN, HORN, t('senda.roca.horn'), t('senda.roca.hornHint'), 'horn', 0, 0, 1.9));
      const m = this.make(-1, ARCH, t('senda.roca.mouth'), t('senda.roca.mouthHint'), 'mouth', r.mouth.x, r.mouth.y, 1.4);
      m.el.disabled = true;
      this.marks.push(m);
    } else if (rr?.mouths) {
      const r = rr.mouths;
      this.stepPips = Array.from({ length: r.need }, () => h('i'));
      this.stepRow.append(h('span.bh-ico', { html: ARCH }), h('span.bh-label', {}, t('senda.roca.shut')), h('span.al-pips.vt-pips', {}, ...this.stepPips), this.stepText);
      this.rows.push(this.ullaRow, this.stepRow, this.hornRow, rule(CRACK, 'senda.roca.rule2'), rule(PAW, 'senda.roca.rule2b'));
      r.arches.forEach((p, k) => {
        const m = this.make(k, ARCH, '', t('senda.roca.archHint'), `arch-${k}`, p.x, p.y, 2.9);
        m.el.classList.add('rk-arch');
        const chip = h('span.rk-cracks', {}, h('i'), h('i'), h('i'));
        m.el.append(chip);
        m.chip = chip;
        this.marks.push(m);
      });
      this.marks.push(this.make(ROCA_HORN, HORN, t('senda.roca.horn'), t('senda.roca.hornHint'), 'horn', 0, 0, 1.9));
    } else if (rr?.bed) {
      this.stepPips = rr.bed.rooms.map(() => h('i'));
      this.stepRow.append(h('span.bh-ico', { html: BONES }), h('span.al-pips.vt-pips', {}, ...this.stepPips), this.stepText);
      this.rows.push(this.stepRow, rule(BONES, 'senda.roca.rule3'));
    } else if (rr?.wolf) {
      const r = rr.wolf;
      this.wolfRow.append(h('span.bh-ico', { html: WOLF }), h('span.bh-label', {}, t('senda.roca.wolf')), h('span.bar.vt-bar.bt-elderbar', {}, this.wolfFill), this.wolfText);
      this.stepPips = [0, 1, 2].map(() => h('i'));
      this.stepRow.append(h('span.bh-ico', { html: WOLF }), h('span.al-pips.vt-pips', {}, ...this.stepPips), this.stepText);
      this.leapRow.append(h('span.bh-ico', { html: LEAP }), this.leapText);
      this.howlRow.append(h('span.bh-ico', { html: HOWL }), this.howlText);
      this.boatRow.append(h('span.bh-ico', { html: BOAT }), this.boatText);
      this.skiffRow.append(h('span.bh-ico', { html: BOAT }), h('span.bh-label', {}, t('senda.barbarian.npc.skiff')), h('span.bar.vt-bar', {}, this.skiffFill), this.skiffText);
      this.rows.push(this.wolfRow, this.stepRow, this.leapRow, this.howlRow, this.hornRow, this.boatRow, this.skiffRow, rule(WATER, 'senda.roca.rule4'));
      r.mouths.forEach((p, k) => {
        if (!r.open[k]) return;
        const m = this.make(100 + k, WATER, '', t('senda.roca.mouthOpen'), `mouth-${k}`, p.x, p.y, 0.6);
        m.el.disabled = true;
        m.el.classList.add('bt-mark', 'bt-tip', 'rk-way');
        if (k === r.trace) m.el.classList.add('rk-bare');
        this.marks.push(m);
      });
      r.edges.forEach((p, k) => {
        const m = this.make(200 + k, WATER, '', t('senda.roca.edgeHint'), `edge-${k}`, p.x, p.y, 0.6);
        m.el.disabled = true;
        m.el.classList.add('bt-mark', 'bt-tip', 'rk-way');
        this.marks.push(m);
      });
      r.pillars.forEach((p, k) => this.marks.push(this.make(WOLF_PILLAR + k, PILLAR, '', t('senda.roca.pillarHint'), `pillar-${k}`, p.x, p.y, 2.4)));
      r.stones.forEach((p, k) => this.marks.push(this.make(WOLF_STONE + k, STONE, '', t('senda.roca.stoneHint'), `stone-${k}`, p.x, p.y, 1.2)));
      const sk = this.make(-2, BOAT, t('senda.barbarian.npc.skiff'), t('senda.roca.skiffHint'), 'skiff', r.skiff.x, r.skiff.y, 0.6);
      sk.el.disabled = true;
      this.marks.push(sk);
      this.marks.push(this.make(ROCA_HORN, HORN, t('senda.roca.horn'), t('senda.roca.hornHint'), 'horn', 0, 0, 1.9));
    }
    this.panel = h(`div.bh.cz-panel.vt-panel.bt-panel.ix-panel.rk-panel.rk-${this.kind}`, {}, ...this.rows);
  }

  private make(at: number, icon: string, name: string, hint: string, test: string, x: number, y: number, lift: number): Mark {
    const state = h('small.al-state.cz-state');
    const el = h(
      `button.al-stone.cz-mark.vt-mark.ix-mark.rk-mark`,
      { type: 'button', 'data-test': `roca-${test}`, 'aria-label': hint, title: hint, onpointerup: (e: Event) => (e.stopPropagation(), at >= 0 && this.mark(at)) },
      h('span.al-ring'),
      h('span.al-glyph', { html: icon }),
      name ? h('b.mn-name.cz-name', {}, name) : '',
      state,
    ) as HTMLButtonElement;
    this.board.append(el);
    return { el, state, point: new THREE.Vector3(), at, kind: test.split('-')[0] as string, x, y, lift };
  }

  private setText(el: HTMLElement, s: string): void {
    if (el.textContent !== s) el.textContent = s;
  }

  private setState(m: Mark, s: string): void {
    if (m.state.textContent !== s) {
      m.state.textContent = s;
      remeasureMark(m.el);
    }
  }

  private bar(s: BattleState, id: string, fill: HTMLElement, text: HTMLElement, row: HTMLElement): void {
    const b = s.senda?.npcs.find((n) => n.id === id);
    if (!b) return;
    const k = Math.max(0, Math.min(1, b.hp / Math.max(1, b.maxHp)));
    setStyle(fill, 'width', `${(k * 100).toFixed(1)}%`);
    this.setText(text, t('senda.hud.hits', { n: b.hp }));
    row.classList.toggle('warn', b.hp * 3 <= b.maxHp);
  }

  /** The horn over Ulla: where she is, and whether it is left. */
  private hornAt(s: BattleState, left: number): void {
    const u = s.senda?.npcs.find((n) => n.id === 'ulla');
    this.setText(this.hornText, left > 0 ? t('senda.roca.hornLeft') : t('senda.roca.hornGone'));
    this.hornRow.classList.toggle('go', left > 0);
    for (const m of this.marks) {
      if (m.kind !== 'horn') continue;
      if (u) {
        m.x = u.x;
        m.y = u.y;
      }
      const gone = left <= 0 || !u || s.result !== 'running';
      m.el.classList.toggle('gone', gone);
      m.el.disabled = gone;
    }
  }

  private updateClimb(s: BattleState): void {
    const r = s.rules?.senda?.roca?.climb;
    const w = s.senda?.roca?.climb;
    if (!r || !w) return;
    this.bar(s, 'ulla', this.ullaFill, this.ullaText, this.ullaRow);
    this.stepPips.forEach((p, i) => p.classList.toggle('on', i < w.ledge || w.done >= 0));
    this.setText(this.stepText, w.done >= 0 ? t('senda.roca.atMouth') : t('senda.roca.ledge', { n: w.ledge + 1, of: r.ledges.length }));
    const hw = w.howler;
    const hs = hw.phase === 2 ? t('senda.roca.rears') : hw.phase === 3 ? t('senda.roca.howls') : hw.phase === 4 ? t('senda.roca.howlerDown') : hw.phase === 0 ? t('senda.roca.howlerAbove') : t('senda.roca.howlIn', { s: secs(hw.next - s.waveT) });
    this.setText(this.howlText, hs);
    this.howlRow.classList.toggle('warn', hw.phase === 2 || hw.phase === 3);
    this.hornAt(s, w.horn);
    for (const m of this.marks) if (m.kind === 'mouth') m.el.classList.toggle('gone', w.done >= 0 || s.result !== 'running');
  }

  private updateMouths(s: BattleState): void {
    const r = s.rules?.senda?.roca?.mouths;
    const w = s.senda?.roca?.mouths;
    if (!r || !w) return;
    this.bar(s, 'ulla', this.ullaFill, this.ullaText, this.ullaRow);
    const shut = w.shut.reduce((a, b) => a + b, 0);
    this.stepPips.forEach((p, i) => p.classList.toggle('on', i < shut));
    this.setText(this.stepText, t('senda.roca.shutN', { n: shut, of: r.need }));
    this.hornAt(s, w.horn);
    // The tracks read up close (the hero within three hexes of a mouth's entrance), and kept read.
    const hero = s.heroes.find((x) => x.downTicks === 0);
    if (hero)
      r.mouths.forEach((p, k) => {
        if (!this.read.has(k) && Math.hypot(hero.x - p.x, hero.y - p.y) < 3200) this.read.add(k);
      });
    const key = `${w.cracks.join('')}|${w.shut.join('')}|${w.job}|${[...this.read].join('')}|${s.result}`;
    if (key === this.key) return;
    this.key = key;
    for (const m of this.marks) {
      if (m.kind !== 'arch') continue;
      const k = m.at;
      const down = !!w.shut[k];
      m.el.classList.toggle('shut', down);
      m.el.disabled = down || s.result !== 'running';
      m.el.classList.toggle('sent', w.job === k);
      m.chip?.querySelectorAll('i').forEach((pip, i) => pip.classList.toggle('on', i < (w.cracks[k] ?? 0)));
      const tracks = this.read.has(k) ? (k === r.trace ? t('senda.roca.tracksPack') : t('senda.roca.tracksWolf')) : '';
      this.setState(m, down ? t('senda.roca.archDown') : tracks);
      m.el.classList.toggle('rk-bare', this.read.has(k) && k === r.trace);
    }
  }

  private updateBed(s: BattleState): void {
    const r = s.rules?.senda?.roca?.bed;
    const w = s.senda?.roca?.bed;
    if (!r || !w) return;
    this.stepPips.forEach((p, i) => p.classList.toggle('on', i < w.cleared));
    const left = w.room >= 0 && w.cleared === w.room ? s.enemies.filter((e) => e.hp > 0 && w.band.includes(e.id)).length : 0;
    this.setText(this.stepText, w.done >= 0 ? t('senda.roca.atBed') : left > 0 ? t('senda.roca.bandLeft', { n: left }) : w.cleared >= r.rooms.length ? t('senda.roca.toBed') : t('senda.roca.room', { n: Math.min(r.rooms.length, w.cleared + 1), of: r.rooms.length }));
    this.stepRow.classList.toggle('warn', left > 0);
  }

  private updateWolf(s: BattleState): void {
    const r = s.rules?.senda?.roca?.wolf;
    const w = s.senda?.roca?.wolf;
    if (!r || !w) return;
    const e = s.enemies.find((x) => x.id === w.id);
    const k = e ? Math.max(0, e.hp) / Math.max(1, e.maxHp) : 0;
    setStyle(this.wolfFill, 'width', `${(k * 100).toFixed(1)}%`);
    const where = (kind: number, n: number): string => (kind === 0 ? t(`senda.roca.mouthN.${n}`) : t(`senda.roca.edgeN.${n}`));
    const st =
      w.mode === 1
        ? t('senda.roca.wLook', { at: where(w.exitKind, w.exit) })
        : w.mode === 2 || w.mode === 11
          ? w.mode === 11
            ? t('senda.roca.wStuck')
            : t('senda.roca.wRun', { at: where(w.exitKind, w.exit) })
          : w.mode === 3
            ? t('senda.roca.wUnder', { s: secs(w.until - s.tick) })
            : w.mode === 4
              ? t('senda.roca.wCut')
              : w.mode === 7
                ? t('senda.roca.wRear')
                : w.mode === 8
                  ? t('senda.roca.wHowl')
                  : w.mode === 9
                    ? t('senda.roca.wUp')
                    : w.mode === 10
                      ? t('senda.roca.wBoat')
                      : w.mode === 5 || w.mode === 6
                        ? t('senda.roca.wLeap')
                        : w.phase === 3
                          ? t('senda.roca.wHunt')
                          : t('senda.roca.wFight');
    this.setText(this.wolfText, st);
    this.wolfRow.classList.toggle('warn', w.mode === 1 || w.mode === 2 || w.mode === 3 || w.mode === 7 || w.mode === 8 || w.mode === 10);
    this.wolfRow.classList.toggle('go', w.mode === 4 || w.mode === 11);
    this.stepPips.forEach((p, i) => {
      p.classList.toggle('on', i < w.phase);
      p.classList.toggle('now', i === w.phase - 1);
    });
    this.setText(this.stepText, t(`senda.roca.phase${w.phase}`));
    const leap = w.mode === 5 || w.mode === 6;
    // (Under the water, up the crack, or with its timer run out while it does something else: no count.)
    const busy = w.mode !== 0 && w.mode !== 4;
    this.setText(this.leapText, leap ? t('senda.roca.leapNow') : busy || w.leapNext <= s.waveT ? t('senda.roca.leapSoon') : t('senda.roca.leapIn', { s: secs(w.leapNext - s.waveT) }));
    this.leapRow.classList.toggle('warn', leap);
    const howl = w.mode === 7 || w.mode === 8;
    this.setText(this.howlText, w.phase === 3 ? t('senda.roca.noHowl') : howl ? t('senda.roca.howlCut') : busy || w.howlNext <= s.waveT ? t('senda.roca.howlSoon') : t('senda.roca.howlIn', { s: secs(w.howlNext - s.waveT) }));
    this.howlRow.classList.toggle('warn', howl);
    this.howlRow.hidden = w.phase === 3;
    this.hornAt(s, w.phase === 3 ? 0 : w.horn);
    this.hornRow.hidden = w.phase === 3;
    this.boatRow.hidden = w.phase === 1;
    if (w.boats >= 0) this.setText(this.boatText, t('senda.roca.boatsAt', { at: where(1, w.boats), s: secs(w.boatsNext - s.waveT) }));
    this.bar(s, 'skiff', this.skiffFill, this.skiffText, this.skiffRow);
    this.skiffRow.hidden = w.phase < 3;
    this.skiffRow.classList.toggle('warn', w.mode === 10);
    const going = w.mode === 1 || w.mode === 2 || w.mode === 11;
    const key = `${w.phase}|${w.mode}|${w.exit}|${w.exitKind}|${w.boats}|${w.pillars.join('')}|${w.stones.join('')}|${w.job}|${s.result}`;
    if (key === this.key) return;
    this.key = key;
    const over = s.result !== 'running' || w.done >= 0;
    for (const m of this.marks) {
      if (m.kind === 'mouth' || m.kind === 'edge') {
        const kind = m.kind === 'mouth' ? 0 : 1;
        const n = m.at - (kind === 0 ? 100 : 200);
        const here = kind === 0 ? w.phase === 1 : w.phase === 2;
        const target = going && w.exitKind === kind && w.exit === n;
        const shut = kind === 1 && edgeShut(r, w, n);
        m.el.classList.toggle('gone', over || !here);
        m.el.classList.toggle('target', target);
        m.el.classList.toggle('shut', shut);
        this.setState(m, target ? t('senda.roca.wayHere') : kind === 0 && n === r.trace ? t('senda.roca.noHeal') : shut ? (n === w.boats ? t('senda.roca.boatsHere') : t('senda.roca.islandShut')) : '');
        continue;
      }
      if (m.kind === 'pillar' || m.kind === 'stone') {
        const stone = m.kind === 'stone';
        const i = m.at - (stone ? WOLF_STONE : WOLF_PILLAR);
        const up = stone ? !!w.stones[i] : !!w.pillars[i];
        const here = stone ? w.phase >= 2 : w.phase === 1;
        m.el.classList.toggle('gone', over || !up || !here || w.phase === 3);
        m.el.disabled = !up;
        m.el.classList.toggle('sent', w.job === m.at);
        m.el.classList.toggle('hot', howl);
        const p = wolfDropAt(r, m.at);
        void p;
        continue;
      }
      if (m.kind === 'skiff') m.el.classList.toggle('gone', over || w.phase < 3);
    }
  }

  update(s: BattleState): void {
    if (this.kind === 'climb') this.updateClimb(s);
    else if (this.kind === 'mouths') this.updateMouths(s);
    else if (this.kind === 'bed') this.updateBed(s);
    else this.updateWolf(s);
  }

  /** Every frame: the marks over their places (lifted to the rock's ground); the way out it eyes held at the edge. */
  place(_s: BattleState | undefined, camera: THREE.Camera, w: number, ht: number): void {
    for (const m of this.marks) {
      if (m.el.classList.contains('gone')) {
        setStyle(m.el, 'display', 'none');
        continue;
      }
      simToWorld(m.x, m.y, m.point, this.ground(m.x, m.y) + m.lift);
      this.v.copy(m.point).project(camera);
      const target = m.el.classList.contains('target');
      if (this.v.z > 1 || (!target && (this.v.x < -1.1 || this.v.x > 1.1 || this.v.y < -1.1 || this.v.y > 1.1))) {
        setStyle(m.el, 'display', 'none');
        continue;
      }
      setStyle(m.el, 'display', '');
      const edge = target && (Math.abs(this.v.x) > 0.92 || Math.abs(this.v.y) > 0.92);
      m.el.classList.toggle('edge', edge);
      const x = (Math.max(-0.92, Math.min(0.92, this.v.x)) * 0.5 + 0.5) * w;
      const y = (-Math.max(-0.92, Math.min(0.92, this.v.y)) * 0.5 + 0.5) * ht;
      pinMark(m.el, Math.min(w - 40, Math.max(40, x)), y);
    }
  }
}
